import { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { WorkspaceEnvVars } from "./WorkspaceEnvVars";

/**
 * Conexões Git (multi-conta) + repositório por workspace (epico 2).
 *
 * Conexões são credenciais reutilizáveis por org: GitHub/GitLab (inclusive
 * self-hosted) e Azure DevOps, por PAT ou OAuth. Cada workspace escolhe uma
 * conexão e a URL do repo. Tokens nunca voltam pra tela.
 */
// ---------------------------------------------------------------------------
// Escopo de organização (sysadmin): quando a página roda DENTRO do Admin com
// uma org selecionada no seletor, todas as chamadas levam ?orgId=<org> - o
// backend (orgForAdmin/adminWorkspace) só honra isso pra sysadmin.
let orgScopeQs = "";
export function setReposOrgScope(orgId?: string): void {
  orgScopeQs = orgId ? `orgId=${encodeURIComponent(orgId)}` : "";
}
function withOrgScope(input: string): string {
  if (!orgScopeQs) return input;
  return input + (input.includes("?") ? "&" : "?") + orgScopeQs;
}
function fetchOrg(input: string, init?: RequestInit): Promise<Response> {
  return fetch(withOrgScope(input), init);
}

interface Connection {
  id: string;
  provider: "github" | "gitlab" | "azure" | "generic";
  host: string;
  label: string;
  authType: "pat" | "oauth";
  accountLogin: string | null;
  hasToken: boolean;
  expiresAt: string | null;
  /** OAuth expirado sem renovação possível - a UI oferece "Reconectar". */
  needsReauth?: boolean;
  createdAt: string;
}
interface RepoStatus {
  url: string;
  branch: string | null;
  provider: string;
  connectionId: string | null;
  hasToken: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: "ok" | "error" | "syncing" | "pending" | null;
  lastSyncError: string | null;
  lastCommit: string | null;
  docCommit?: string | null;
  docAt?: string | null;
  docStale?: boolean;
}
interface WorkspaceRow {
  id: string;
  name: string;
  slug: string;
  repos: RepoStatus[];
  autoSync: boolean;
  autoDocs: boolean;
  docsAt: string | null;
  docsStatus: "running" | "idle";
  groupLabels: Record<string, string>;
}
/** Entrada da documentação gerada (workspace/grupo/repo). */
interface DocEntry {
  relPath: string;
  kind: "workspace" | "group" | "repo";
  group: string | null;
  title: string;
  generatedAt: string | null;
}
/** Repo listado da conexão: pessoal ou de uma organização (`group`). */
interface RepoOption {
  name: string;
  url: string;
  private: boolean;
  group: string;
}
/** Rascunho de um repo na edição de um workspace (multi-repo). */
interface RepoDraft {
  url: string;
  branch: string;
  connectionId: string;
  /** token inline p/ repo público/privado adicionado por URL (sem conexão). */
  token?: string;
}

/** Normaliza um workspace do backend (aceita `repos` multi ou o legado `repo`). */
interface RawWorkspace {
  id: string; name: string; slug: string;
  repos?: RepoStatus[]; repo?: RepoStatus | null;
  autoSync?: boolean; autoDocs?: boolean; docsAt?: string | null; docsStatus?: "running" | "idle";
  groupLabels?: Record<string, string>;
}
function toRow(w: RawWorkspace): WorkspaceRow {
  return {
    id: w.id,
    name: w.name,
    slug: w.slug,
    repos: w.repos ?? (w.repo ? [w.repo] : []),
    autoSync: w.autoSync !== false,
    autoDocs: w.autoDocs !== false,
    docsAt: w.docsAt ?? null,
    docsStatus: w.docsStatus ?? "idle",
    groupLabels: w.groupLabels ?? {},
  };
}

export function ReposPage({
  onClose,
  isSysadmin = false,
  orgId,
  embedded = false,
}: {
  onClose: () => void;
  isSysadmin?: boolean;
  /** Sysadmin (via Admin): gerencia os repositórios da org escolhida no seletor. */
  orgId?: string;
  /** Renderização dentro do Admin (sem header/moldura própria). */
  embedded?: boolean;
}) {
  // escopo PRIMEIRO (antes de qualquer fetch dos efeitos abaixo)
  setReposOrgScope(orgId);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [oauthCfg, setOauthCfg] = useState<{ github: boolean; gitlab: boolean; azure: boolean }>({ github: false, gitlab: false, azure: false });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ ok: boolean; msg: string } | null>(null);
  // intervalo EFETIVO do auto-sync da org (override do sysadmin ?? default) -
  // exibido no toggle; antes era "15 min" fixo e mentia quando configurado.
  const [syncMinutes, setSyncMinutes] = useState<number | null>(null);

  // Atualização CIRÚRGICA de um workspace (nunca recarrega a página toda) - evita
  // o "pisca-pisca" da seção inteira a cada ação/evento de documentação.
  const refreshWorkspace = useCallback(async (wsId: string) => {
    try {
      const r = await fetchOrg(`/api/admin/workspaces/${wsId}`);
      if (!r.ok) return;
      const j = await r.json();
      if (j.workspace) setWorkspaces((prev) => prev.map((w) => (w.id === wsId ? toRow(j.workspace) : w)));
    } catch {
      /* mantém o estado atual */
    }
  }, [orgId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orgR, connR] = await Promise.all([fetchOrg("/api/org"), fetchOrg("/api/admin/connections")]);
      const org = orgR.ok ? await orgR.json() : { workspaces: [] };
      const conn = connR.ok ? await connR.json() : { items: [], oauth: {} };
      setWorkspaces((org.workspaces ?? []).map(toRow));
      setSyncMinutes(org.limits?.effectiveRepoSyncMinutes ?? null);
      setConnections(conn.items ?? []);
      setOauthCfg({ github: !!conn.oauth?.github, gitlab: !!conn.oauth?.gitlab, azure: !!conn.oauth?.azure });
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    // feedback do retorno do OAuth (?oauth=ok&msg=...)
    const p = new URLSearchParams(window.location.search);
    if (p.get("oauth")) {
      setFlash({ ok: p.get("oauth") === "ok", msg: p.get("msg") || "" });
      if (!embedded) window.history.replaceState({}, "", "/repos");
    }
    void load();
  }, [load, embedded]);

  const body = (
    <>

      {flash && (
        <div className={flash.ok ? "auth-ok" : "auth-error"} style={{ marginBottom: 12 }}>
          {flash.ok ? "✓ " : "✗ "}{flash.msg}
        </div>
      )}
      {err && <div className="auth-error" style={{ marginBottom: 12 }}>{err}</div>}

      <ConnectionsSection
        connections={connections}
        oauthCfg={oauthCfg}
        onChanged={load}
        setErr={setErr}
      />

      <h2 style={{ marginTop: 24 }}>Workspaces</h2>
      <p className="admin-user-meta" style={{ marginBottom: 12 }}>
        Escolha uma conexão e a URL do repositório. O código é clonado (read-only) e atualizado periodicamente.
      </p>
      {loading ? (
        <div className="admin-empty">Carregando…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {workspaces.map((w) => (
            <WorkspaceCard key={w.id} ws={w} connections={connections} onRefresh={refreshWorkspace} setErr={setErr} syncMinutes={syncMinutes} isSysadmin={isSysadmin} />
          ))}
        </div>
      )}

      {isSysadmin && <TrashPanel setErr={setErr} />}
    </>
  );

  if (embedded) return <div className="repos-embedded">{body}</div>;
  return (
    <div className="about-page">
      <div className="about-header">
        <h1>Repositórios</h1>
        <button className="about-close" onClick={onClose} type="button" aria-label="Voltar ao chat">×</button>
      </div>
      {body}
    </div>
  );
}

function StatusBadge({ s }: { s: RepoStatus["lastSyncStatus"] | null }) {
  const cls = s === "ok" ? "ok" : s === "error" ? "warn" : "";
  const label = s === "ok" ? "sincronizado" : s === "error" ? "erro" : s === "syncing" ? "sincronizando" : s ? "pendente" : "sem repo";
  return <span className={`badge ${cls}`}>{label}</span>;
}

/** Spinner inline (CSS). Usado dentro dos botões enquanto a ação roda. */
function Spinner() {
  return <span className="repo-spin" aria-hidden="true" />;
}

/** Tempo relativo curto ("há 3 min", "há 2 h", "há 5 d"). */
function relTime(ts: string | null | undefined): string {
  if (!ts) return "nunca";
  const d = new Date(ts).getTime();
  if (Number.isNaN(d)) return String(ts);
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 45) return "agora";
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `há ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `há ${days} d`;
  return new Date(ts).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

/** Nome curto do repo a partir da URL (ex.: https://host/org/sub/api.git -> sub/api). */
function shortRepoName(urlOrName: string): string {
  const s = urlOrName.replace(/\.git$/i, "");
  const parts = s.replace(/^https?:\/\/[^/]+\//i, "").split("/").filter(Boolean);
  return parts.length > 1 ? parts.slice(1).join("/") : parts[parts.length - 1] || s;
}

/** Caminho completo grupo/.../repo a partir da URL (deixa o grupo visível). */
/**
 * Segmentos "lógicos" do path do repo (espelha repoPathSegments do backend):
 * decodifica %XX e normaliza Azure DevOps (ORG/PROJETO/_git/REPO -> PROJETO/REPO)
 * - o grupo real é o PROJETO, não a organização da conta.
 */
function repoPathSegmentsFE(url: string): string[] {
  let noProto = url.replace(/^https?:\/\/[^/]+\//i, "").replace(/\.git$/i, "");
  try {
    noProto = decodeURIComponent(noProto);
  } catch {
    // %XX malformado - segue com o literal
  }
  const segs = noProto.split("/").filter((s) => s && s !== "." && s !== "..");
  const gitIdx = segs.indexOf("_git");
  if (gitIdx > 0) {
    segs.splice(gitIdx, 1);
    if (gitIdx >= 2) segs.shift();
  }
  return segs;
}

function repoPathName(url: string): string {
  const segs = repoPathSegmentsFE(url);
  return segs.length ? segs.join("/") : url;
}

/** Grupo (namespace de topo) a partir da URL. */
function groupOfUrl(url: string): string {
  return repoPathSegmentsFE(url)[0] || "repo";
}

/** Caminho relativo do doc do repo (espelha repoRelPath do backend: minúsculo/sanitizado). */
function repoRelPathFE(url: string): string {
  const segs = repoPathSegmentsFE(url)
    .map((s) => s.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase())
    .filter((s) => s && s !== "." && s !== "..");
  return segs.length ? segs.join("/") : "repo";
}
/** Chave de diretório do grupo (primeiro segmento sanitizado) - casa com o backend. */
function groupDirOf(url: string): string {
  return repoRelPathFE(url).split("/")[0] || "repo";
}
/** relPath do doc de um repo / grupo / workspace. */
function repoDocRel(url: string): string { return `${repoRelPathFE(url)}.md`; }
function groupDocRel(url: string): string { return `${groupDirOf(url)}/_grupo.md`; }
const WORKSPACE_DOC_REL = "_workspace.md";

/** Agrupa repos por grupo, preservando a ordem de aparição dos grupos. */
function groupRepos(repos: RepoStatus[]): [string, RepoStatus[]][] {
  const map = new Map<string, RepoStatus[]>();
  for (const r of repos) {
    const g = groupOfUrl(r.url);
    if (!map.has(g)) map.set(g, []);
    map.get(g)!.push(r);
  }
  return [...map.entries()];
}

// grupos minimizados persistidos por workspace (voltam ao reentrar)
const COLLAPSED_KEY = "educon-assistant-repo-collapsed";
function readCollapsed(wsId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${COLLAPSED_KEY}:${wsId}`);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}
function writeCollapsed(wsId: string, set: Set<string>): void {
  try {
    localStorage.setItem(`${COLLAPSED_KEY}:${wsId}`, JSON.stringify([...set]));
  } catch {
    /* quota/privado - ignora */
  }
}

/** Modal de confirmação customizado (sem `window.confirm` nativo). */
function ConfirmModal({
  title,
  message,
  confirmLabel = "Confirmar",
  onConfirm,
  onClose,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal prompt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <header className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar" type="button">×</button>
        </header>
        <p style={{ padding: "0 16px", margin: "8px 0 0" }}>{message}</p>
        <div className="admin-user-actions" style={{ padding: 16, justifyContent: "flex-end" }}>
          <button className="btn-reject" type="button" onClick={onClose}>Cancelar</button>
          <button className="btn-approve" type="button" autoFocus onClick={() => { onConfirm(); onClose(); }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/** Modal de entrada de texto customizado (sem `window.prompt` nativo). */
function PromptModal({
  title,
  label,
  initial = "",
  placeholder,
  submitLabel = "Salvar",
  onSubmit,
  onClose,
}: {
  title: string;
  label?: string;
  initial?: string;
  placeholder?: string;
  submitLabel?: string;
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal prompt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <header className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar" type="button">×</button>
        </header>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {label && <span className="admin-user-meta">{label}</span>}
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            spellCheck={false}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); onSubmit(value); onClose(); }
              else if (e.key === "Escape") onClose();
            }}
          />
          <div className="admin-user-actions" style={{ justifyContent: "flex-end" }}>
            <button className="btn-reject" type="button" onClick={onClose}>Cancelar</button>
            <button className="btn-approve" type="button" onClick={() => { onSubmit(value); onClose(); }}>{submitLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Remove a cerca ```markdown ... ``` que envolve o doc inteiro (defensivo). */
function stripMarkdownFence(md: string): string {
  const t = md.trim();
  const m = t.match(/^```(?:markdown|md)?[ \t]*\r?\n([\s\S]*?)\r?\n```$/i);
  return m && m[1] != null ? m[1].trim() : md;
}

/** Baixa um texto como arquivo (ex.: markdown) no navegador. */
function downloadText(filename: string, text: string): void {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Seletor de repositórios por nome: busca + chips (mesmo estilo dos badges de
 * permissão), agrupados por "Pessoal" e por organização. Multi-seleção: cada
 * clique marca/desmarca um repo - dá pra montar o workspace com vários de uma vez.
 */
function RepoPicker({
  options,
  loading,
  warnings,
  selectedUrls,
  onToggle,
  onToggleGroup,
}: {
  options: RepoOption[];
  loading: boolean;
  warnings: string[];
  selectedUrls: Set<string>;
  onToggle: (url: string) => void;
  onToggleGroup: (urls: string[], select: boolean) => void;
}) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = term ? options.filter((o) => o.name.toLowerCase().includes(term)) : options;
    const groups = new Map<string, RepoOption[]>();
    for (const o of list) {
      if (!groups.has(o.group)) groups.set(o.group, []);
      groups.get(o.group)!.push(o);
    }
    return [...groups.entries()].sort(([a], [b]) => (a === "Pessoal" ? -1 : b === "Pessoal" ? 1 : a.localeCompare(b)));
  }, [options, q]);

  const selectedCount = selectedUrls.size;

  return (
    <div className="field">
      <span>
        Repositórios{" "}
        {loading
          ? "(carregando…)"
          : options.length
            ? `(${options.length} disponíveis — clique para marcar vários${selectedCount ? `; ${selectedCount} selecionado${selectedCount > 1 ? "s" : ""}` : ""})`
            : "(nenhum listado — cole a URL abaixo)"}
      </span>
      {options.length > 6 && (
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar pelo nome…"
          spellCheck={false}
          style={{ marginBottom: 6 }}
        />
      )}
      <div className="repo-picker">
        {loading ? (
          <span className="admin-user-meta">carregando repositórios…</span>
        ) : filtered.length === 0 ? (
          <span className="admin-user-meta">{q ? "nada encontrado" : "nenhum repositório listado"}</span>
        ) : (
          filtered.map(([group, repos]) => {
            const groupUrls = repos.map((r) => r.url);
            const selInGroup = groupUrls.filter((u) => selectedUrls.has(u)).length;
            const allSel = selInGroup === groupUrls.length && groupUrls.length > 0;
            return (
            <div key={group} className="repo-picker-group">
              <button
                type="button"
                className="repo-picker-group-label"
                onClick={() => onToggleGroup(groupUrls, !allSel)}
                title={allSel ? "Desmarcar todos do grupo" : "Marcar todos do grupo"}
                style={{ cursor: "pointer", background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left" }}
              >
                {allSel ? "✓ " : selInGroup ? "– " : "○ "}{group} ({selInGroup}/{groupUrls.length})
              </button>
              <div className="perm-badge-row">
                {repos.map((o) => {
                  const on = selectedUrls.has(o.url);
                  return (
                    <button
                      key={o.url}
                      type="button"
                      className={`perm-badge ${on ? "active" : ""}`}
                      aria-pressed={on}
                      title={o.url}
                      onClick={() => onToggle(o.url)}
                    >
                      {on ? "✓ " : ""}{o.private ? "🔒 " : ""}
                      {o.name.includes("/") ? o.name.split("/").slice(1).join("/") : o.name}
                    </button>
                  );
                })}
              </div>
            </div>
            );
          })
        )}
      </div>
      {warnings.length > 0 && (
        <p className="admin-user-meta" style={{ marginTop: 6 }}>
          {warnings.length === 1 ? "A organização" : "As organizações"} <strong>{warnings.join(", ")}</strong> restringe(m)
          acesso de apps OAuth de terceiros - peça ao dono dela pra autorizar este app (GitHub → Settings →
          Third-party access) pra esses repos aparecerem aqui.
        </p>
      )}
    </div>
  );
}

function ConnectionsSection({
  connections,
  oauthCfg,
  onChanged,
  setErr,
}: {
  connections: Connection[];
  oauthCfg: { github: boolean; gitlab: boolean; azure: boolean };
  onChanged: () => void;
  setErr: (e: string | null) => void;
}) {
  const [provider, setProvider] = useState<Connection["provider"]>("github");
  const [host, setHost] = useState("");
  const [label, setLabel] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState<{ title: string; message: string; confirmLabel?: string; onConfirm: () => void } | null>(null);

  const addConnection = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetchOrg("/api/admin/connections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, host: host.trim() || undefined, label: label.trim() || undefined, token: token.trim() || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) setErr(j.error || "falha ao criar conexão");
      else {
        setHost(""); setLabel(""); setToken("");
      }
    } finally {
      setBusy(false);
      onChanged();
    }
  };

  const linkOAuth = async (p: "github" | "gitlab" | "azure") => {
    setErr(null);
    const r = await fetchOrg(`/api/admin/connections/oauth/${p}/start?label=${encodeURIComponent(`${p} (OAuth)`)}`);
    const j = await r.json().catch(() => ({}));
    if (r.ok && j.url) window.location.href = j.url; // vai pro provedor autorizar
    else setErr(j.error || "OAuth indisponível");
  };

  /** Re-autoriza uma conexão OAuth expirada: mesmo fluxo, mas ATUALIZA a conexão (mesmo id). */
  const reconnectOAuth = async (c: Connection) => {
    if (c.provider === "generic") return;
    setErr(null);
    const r = await fetchOrg(
      `/api/admin/connections/oauth/${c.provider}/start?label=${encodeURIComponent(c.label)}&reconnect=${encodeURIComponent(c.id)}`,
    );
    const j = await r.json().catch(() => ({}));
    if (r.ok && j.url) window.location.href = j.url;
    else setErr(j.error || "OAuth indisponível");
  };

  const doRemove = async (id: string) => {
    await fetchOrg(`/api/admin/connections/${id}`, { method: "DELETE" });
    onChanged();
  };
  const remove = (id: string, label: string) =>
    setConfirmState({
      title: "Remover conexão",
      message: `Remover a conexão "${label}"?`,
      confirmLabel: "Remover",
      onConfirm: () => void doRemove(id),
    });

  return (
    <div className="admin-block" style={{ margin: 0 }}>
      <h3>Conexões Git</h3>
      {connections.length === 0 ? (
        <div className="admin-empty" style={{ padding: "8px 0" }}>Nenhuma conexão ainda.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          {connections.map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span className="badge">{c.provider}</span>
              <strong>{c.label}</strong>
              <span className="admin-user-meta">
                {c.host}{c.accountLogin ? ` · @${c.accountLogin}` : ""} · {c.authType.toUpperCase()}
              </span>
              {c.needsReauth && (
                <span className="badge warn" title="O token OAuth expirou e não pôde ser renovado - re-autorize a conta.">
                  autenticação expirada
                </span>
              )}
              {c.needsReauth && c.authType === "oauth" && (
                <button className="btn-approve" disabled={busy} onClick={() => void reconnectOAuth(c)} type="button">
                  Reconectar
                </button>
              )}
              <button className="btn-reject" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => remove(c.id, c.label)} type="button">Remover</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", maxWidth: 760 }}>
        <label className="field" style={{ width: 130 }}>
          <span>Provedor</span>
          <select value={provider} onChange={(e) => setProvider(e.target.value as Connection["provider"])}>
            <option value="github">GitHub</option>
            <option value="gitlab">GitLab</option>
            <option value="azure">Azure DevOps</option>
            <option value="generic">Genérico</option>
          </select>
        </label>
        <label className="field" style={{ flex: "1 1 160px" }}>
          <span>Host (self-hosted, opcional)</span>
          <input value={host} onChange={(e) => setHost(e.target.value)} placeholder={provider === "github" ? "github.com" : provider === "gitlab" ? "gitlab.com" : provider === "azure" ? "dev.azure.com" : "host"} spellCheck={false} />
        </label>
        <label className="field" style={{ flex: "1 1 160px" }}>
          <span>Rótulo</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex.: GitHub - Cliente A" spellCheck={false} />
        </label>
        <label className="field" style={{ flex: "1 1 200px" }}>
          <span>Token (PAT)</span>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="cole o PAT" spellCheck={false} />
        </label>
        <button className="btn-approve" disabled={busy} onClick={addConnection} type="button">Adicionar (PAT)</button>
      </div>
      <p className="admin-user-meta" style={{ marginTop: 6 }}>
        Ou vincule <strong>por OAuth</strong> (abaixo): você autoriza no provedor e o token volta sozinho - sem colar PAT.
      </p>

      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button className="btn-reject" onClick={() => linkOAuth("github")} type="button" disabled={!oauthCfg.github} title={oauthCfg.github ? "" : "OAuth do GitHub não configurado na plataforma"}>
          Vincular GitHub (OAuth)
        </button>
        <button className="btn-reject" onClick={() => linkOAuth("gitlab")} type="button" disabled={!oauthCfg.gitlab} title={oauthCfg.gitlab ? "" : "OAuth do GitLab não configurado na plataforma"}>
          Vincular GitLab (OAuth)
        </button>
        <button className="btn-reject" onClick={() => linkOAuth("azure")} type="button" disabled={!oauthCfg.azure} title={oauthCfg.azure ? "" : "OAuth do Azure DevOps (Entra ID) não configurado na plataforma"}>
          Vincular Azure DevOps (OAuth)
        </button>
      </div>

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onClose={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

function WorkspaceCard({
  ws,
  connections,
  onRefresh,
  setErr,
  syncMinutes,
  isSysadmin = false,
}: {
  ws: WorkspaceRow;
  connections: Connection[];
  onRefresh: (wsId: string) => Promise<void> | void;
  /** Intervalo efetivo do auto-sync (config da org/plataforma) - só exibição. */
  syncMinutes?: number | null;
  setErr: (e: string | null) => void;
  /** Sysadmin: habilita o download do código do repo (.tar.gz). */
  isSysadmin?: boolean;
}) {
  const [editing, setEditing] = useState(ws.repos.length === 0);
  // rascunho da lista de repos do workspace (multi-repo)
  const [drafts, setDrafts] = useState<RepoDraft[]>(
    ws.repos.map((r) => ({ url: r.url, branch: r.branch ?? "", connectionId: r.connectionId ?? "" })),
  );
  const [connectionId, setConnectionId] = useState(ws.repos[0]?.connectionId ?? "");
  const [manualUrl, setManualUrl] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [repoOptions, setRepoOptions] = useState<RepoOption[]>([]);
  const [repoWarnings, setRepoWarnings] = useState<string[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  // modais customizados (sem diálogos nativos do browser)
  const [confirmState, setConfirmState] = useState<{ title: string; message: string; confirmLabel?: string; onConfirm: () => void } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [viewDoc, setViewDoc] = useState<{ relPath: string; title: string } | null>(null);
  // estado granular de geração de docs (SSE) e sync em andamento (spinner no item)
  const [docState, setDocState] = useState<{ status: "running" | "idle"; repos: Set<string>; groups: Set<string>; workspace: boolean }>(
    { status: ws.docsStatus, repos: new Set(), groups: new Set(), workspace: false },
  );
  const [syncingUrls, setSyncingUrls] = useState<Set<string>>(new Set());
  const [syncingAll, setSyncingAll] = useState(false);
  const [filter, setFilter] = useState(""); // busca interna dos repos
  // grupos minimizados (persistidos por workspace)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed(ws.id));
  const toggleCollapse = (g: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      writeCollapsed(ws.id, next);
      return next;
    });

  // SSE: status granular da documentação (spinner no botão certo), sem polling.
  useEffect(() => {
    const es = new EventSource(withOrgScope(`/api/admin/workspaces/${ws.id}/docs/events`));
    es.addEventListener("status", (ev) => {
      try {
        const e = JSON.parse((ev as MessageEvent).data) as { status: "running" | "idle"; repos?: string[]; groups?: string[]; workspace?: boolean; updated?: boolean };
        setDocState({ status: e.status, repos: new Set(e.repos ?? []), groups: new Set(e.groups ?? []), workspace: !!e.workspace });
        if (e.updated) void onRefresh(ws.id); // atualiza docAt/docStale cirurgicamente (sem recarregar tudo)
      } catch {
        /* evento malformado - ignora */
      }
    });
    es.onerror = () => { /* o navegador reconecta sozinho */ };
    return () => es.close();
  }, [ws.id, onRefresh]);

  // ao escolher uma conexão (modo edição), lista os repos acessíveis
  useEffect(() => {
    if (!editing || !connectionId) { setRepoOptions([]); setRepoWarnings([]); return; }
    let alive = true;
    setLoadingRepos(true);
    fetchOrg(`/api/admin/connections/${connectionId}/repos`)
      .then((r) => (r.ok ? r.json() : { items: [], warnings: [] }))
      .then((j) => { if (alive) { setRepoOptions(j.items ?? []); setRepoWarnings(j.warnings ?? []); } })
      .catch(() => { if (alive) { setRepoOptions([]); setRepoWarnings([]); } })
      .finally(() => { if (alive) setLoadingRepos(false); });
    return () => { alive = false; };
  }, [editing, connectionId]);

  const selectedUrls = useMemo(() => new Set(drafts.map((d) => d.url)), [drafts]);

  const toggleRepo = (url: string) =>
    setDrafts((ds) =>
      ds.some((d) => d.url === url) ? ds.filter((d) => d.url !== url) : [...ds, { url, branch: "", connectionId }],
    );
  const toggleGroup = (urls: string[], select: boolean) =>
    setDrafts((ds) => {
      if (!select) { const rm = new Set(urls); return ds.filter((d) => !rm.has(d.url)); }
      const have = new Set(ds.map((d) => d.url));
      return [...ds, ...urls.filter((u) => !have.has(u)).map((u) => ({ url: u, branch: "", connectionId }))];
    });
  const addManual = () => {
    const u = manualUrl.trim();
    if (!u) return;
    if (!drafts.some((d) => d.url === u)) setDrafts((ds) => [...ds, { url: u, branch: "", connectionId, token: manualToken.trim() || undefined }]);
    setManualUrl(""); setManualToken("");
  };
  const removeDraft = (url: string) => setDrafts((ds) => ds.filter((d) => d.url !== url));
  const patchDraft = (url: string, patch: Partial<RepoDraft>) =>
    setDrafts((ds) => ds.map((d) => (d.url === url ? { ...d, ...patch } : d)));

  // ação mutante: aguarda e atualiza SÓ este workspace (nunca a página toda)
  const mutate = async (path: string, method: string, body?: unknown): Promise<boolean> => {
    setBusy(true); setErr(null);
    try {
      const r = await fetchOrg(path, {
        method,
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setErr(j.error || "falha"); return false; }
      return true;
    } finally {
      setBusy(false);
      await onRefresh(ws.id);
    }
  };

  // regerar doc: fire-and-forget. O SSE mostra o spinner no item - NÃO recarrega.
  const regen = (scope: string) => {
    setErr(null);
    void fetchOrg(`/api/admin/workspaces/${ws.id}/docs/regenerate${scope}`, { method: "POST" })
      .then((r) => { if (!r.ok) r.json().then((j) => setErr(j.error || "falha ao gerar")).catch(() => {}); });
  };

  const syncRepo = async (url: string) => {
    setErr(null);
    setSyncingUrls((s) => new Set(s).add(url));
    try {
      const r = await fetchOrg(`/api/admin/workspaces/${ws.id}/repo/sync?url=${encodeURIComponent(url)}`, { method: "POST" });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setErr(j.error || "falha no sync"); }
    } finally {
      setSyncingUrls((s) => { const n = new Set(s); n.delete(url); return n; });
      await onRefresh(ws.id);
    }
  };
  const syncAll = async () => {
    setErr(null); setSyncingAll(true);
    try {
      const r = await fetchOrg(`/api/admin/workspaces/${ws.id}/repo/sync`, { method: "POST" });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setErr(j.error || "falha no sync"); }
    } finally { setSyncingAll(false); await onRefresh(ws.id); }
  };

  const save = () => {
    if (drafts.length === 0) { setErr("selecione ou cole ao menos um repositório"); return; }
    void (async () => {
      const ok = await mutate(`/api/admin/workspaces/${ws.id}/repo`, "PUT", {
        repos: drafts.map((d) => ({
          url: d.url,
          branch: d.branch.trim() || undefined,
          connectionId: d.connectionId || undefined,
          token: !d.connectionId && d.token?.trim() ? d.token.trim() : undefined,
        })),
      });
      if (ok) setEditing(false);
    })();
  };

  const submitRename = (label: string) => {
    if (!renaming) return;
    void mutate(`/api/admin/workspaces/${ws.id}/group-label`, "PUT", { group: renaming, label });
  };

  const downloadAllDocs = async () => {
    setErr(null);
    try {
      const r = await fetchOrg(`/api/admin/workspaces/${ws.id}/docs`);
      const j = await r.json().catch(() => ({ items: [] }));
      const items: DocEntry[] = j.items ?? [];
      const parts: string[] = [];
      for (const d of items) {
        const rr = await fetchOrg(`/api/admin/workspaces/${ws.id}/docs/content?path=${encodeURIComponent(d.relPath)}`);
        const jj = await rr.json().catch(() => ({}));
        if (rr.ok && typeof jj.content === "string") parts.push(`<!-- ${d.relPath} -->\n\n${jj.content}`);
      }
      if (parts.length) downloadText(`${ws.slug || ws.id}-documentacao.md`, parts.join("\n\n---\n\n"));
      else setErr("nenhuma documentação gerada ainda");
    } catch {
      setErr("falha ao baixar a documentação");
    }
  };

  const connLabelOf = (cid: string | null) =>
    cid ? connections.find((c) => c.id === cid)?.label ?? "(conexão removida)" : null;

  const syncedCount = ws.repos.filter((r) => r.lastSyncStatus === "ok").length;
  const errorCount = ws.repos.filter((r) => r.lastSyncStatus === "error").length;
  const staleDocs = ws.repos.filter((r) => r.docStale).length;
  const term = filter.trim().toLowerCase();
  const visibleGroups = groupRepos(ws.repos)
    .map(([g, repos]) => [g, term ? repos.filter((r) => repoPathName(r.url).toLowerCase().includes(term)) : repos] as [string, RepoStatus[]])
    .filter(([, repos]) => repos.length > 0);

  return (
    <div className="admin-block repo-ws" style={{ margin: 0 }}>
      <div className="repo-ws-head">
        <h3 className="repo-ws-title">{ws.name}</h3>
        {ws.repos.length > 0 && (
          <div className="repo-ws-stats">
            <span className="repo-pill ok" title="sincronizados">✓ {syncedCount}</span>
            {errorCount > 0 && <span className="repo-pill err" title="com erro">✕ {errorCount}</span>}
            {staleDocs > 0 && <span className="repo-pill warn" title="docs desatualizadas">doc {staleDocs}</span>}
            <span className="repo-pill">{ws.repos.length} repo{ws.repos.length > 1 ? "s" : ""}</span>
            {docState.status === "running" && <span className="repo-pill gen"><Spinner /> gerando docs</span>}
          </div>
        )}
      </div>

      {ws.repos.length > 0 && !editing ? (
        <>
          {ws.repos.length > 6 && (
            <input
              className="repo-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filtrar repositórios…"
              spellCheck={false}
            />
          )}

          <div className="repo-groups">
            {visibleGroups.map(([group, repos]) => {
              const isCollapsed = collapsed.has(group) && !term; // busca força expandir
              const label = ws.groupLabels[group] ?? group;
              const firstUrl = repos[0]?.url ?? "";
              const groupGen = docState.groups.has(groupDirOf(firstUrl));
              return (
                <div className="repo-group" key={group}>
                  <div className="repo-group-head">
                    <button
                      type="button"
                      className="repo-group-toggle"
                      onClick={() => toggleCollapse(group)}
                      title={isCollapsed ? "Expandir grupo" : "Minimizar grupo"}
                    >
                      <span className="repo-group-caret">{isCollapsed ? "▸" : "▾"}</span>
                      <span className="repo-group-name">{label}</span>
                      {label !== group && <span className="repo-group-real">({group})</span>}
                      <span className="repo-group-count">{repos.length}</span>
                    </button>
                    <div className="repo-group-actions">
                      <button className="icon-btn" disabled={busy} onClick={() => setViewDoc({ relPath: groupDocRel(firstUrl), title: `Grupo ${label}` })} type="button" title="Ver doc do grupo">📄</button>
                      <button className="icon-btn" disabled={groupGen} onClick={() => regen(`?group=${encodeURIComponent(group)}`)} type="button" title="Regerar doc do grupo">
                        {groupGen ? <Spinner /> : "↻ doc"}
                      </button>
                      <button className="icon-btn" disabled={busy} onClick={() => setRenaming(group)} type="button" title="Renomear grupo">✎</button>
                      <button className="icon-btn danger" disabled={busy} onClick={() => setConfirmState({ title: "Remover grupo", message: `Remover o grupo "${label}" e TODOS os seus ${repos.length} repositório(s) de "${ws.name}"? Os clones serão apagados.`, confirmLabel: "Remover grupo", onConfirm: () => void mutate(`/api/admin/workspaces/${ws.id}/repo?group=${encodeURIComponent(group)}`, "DELETE") })} type="button" title="Remover grupo inteiro">🗑</button>
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="repo-list">
                      {repos.map((repo) => {
                        const repoGen = docState.repos.has(repo.url);
                        const isSyncing = syncingUrls.has(repo.url) || repo.lastSyncStatus === "syncing";
                        return (
                          <div key={repo.url} className="repo-item">
                            <div className="repo-item-main">
                              <StatusBadge s={repo.lastSyncStatus} />
                              <span className="repo-item-name" title={repo.url}>{repoPathName(repo.url)}</span>
                              <span className="repo-item-meta">
                                {repo.branch || "default"}
                                {repo.lastCommit ? ` · ${repo.lastCommit}` : ""} · {relTime(repo.lastSyncAt)}
                                {connLabelOf(repo.connectionId) ? ` · ${connLabelOf(repo.connectionId)}` : ""}
                              </span>
                              {repoGen ? (
                                <span className="repo-doc-tag gen"><Spinner /> gerando doc</span>
                              ) : repo.docStale ? (
                                <span className="repo-doc-tag stale">doc desatualizada</span>
                              ) : repo.docAt ? (
                                <span className="repo-doc-tag ok">doc {relTime(repo.docAt)}</span>
                              ) : null}
                            </div>
                            <div className="repo-item-actions">
                              <button className="icon-btn" disabled={busy} onClick={() => setViewDoc({ relPath: repoDocRel(repo.url), title: repoPathName(repo.url) })} type="button" title="Ver documentação">📄</button>
                              <button className="icon-btn" disabled={repoGen} onClick={() => regen(`?url=${encodeURIComponent(repo.url)}`)} type="button" title="Regerar documentação">
                                {repoGen ? <Spinner /> : "↻ doc"}
                              </button>
                              <button className="icon-btn" disabled={isSyncing} onClick={() => void syncRepo(repo.url)} type="button" title="Sincronizar">
                                {isSyncing ? <Spinner /> : "⟳ sync"}
                              </button>
                              {isSysadmin && repo.lastCommit && (
                                <a
                                  className="icon-btn"
                                  href={withOrgScope(`/api/admin/workspaces/${ws.id}/repo/archive?url=${encodeURIComponent(repo.url)}`)}
                                  title="Baixar código (.tar.gz)"
                                >
                                  ⬇ .tar.gz
                                </a>
                              )}
                              <button className="icon-btn danger" disabled={busy} onClick={() => setConfirmState({ title: "Remover repositório", message: `Remover ${shortRepoName(repo.url)} de "${ws.name}"? O clone será apagado.`, confirmLabel: "Remover", onConfirm: () => void mutate(`/api/admin/workspaces/${ws.id}/repo?url=${encodeURIComponent(repo.url)}`, "DELETE") })} type="button" title="Remover repositório">🗑</button>
                            </div>
                            {repo.lastSyncStatus === "error" && repo.lastSyncError && (
                              <div className="repo-item-error">{repo.lastSyncError}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {visibleGroups.length === 0 && <div className="admin-user-meta">nenhum repositório encontrado para "{filter}".</div>}
          </div>

          <div className="repo-toggles">
            <label className="repo-toggle">
              <input type="checkbox" checked={ws.autoSync} disabled={busy} onChange={(e) => void mutate(`/api/admin/workspaces/${ws.id}/settings`, "PUT", { autoSync: e.target.checked })} />
              <span>Auto-sincronizar{syncMinutes ? ` (a cada ${syncMinutes} min)` : ""}</span>
            </label>
            <label className="repo-toggle">
              <input type="checkbox" checked={ws.autoDocs} disabled={busy} onChange={(e) => void mutate(`/api/admin/workspaces/${ws.id}/settings`, "PUT", { autoDocs: e.target.checked })} />
              <span>Auto-documentar quando um repo muda</span>
            </label>
          </div>

          <WorkspaceEnvVars orgQs={orgScopeQs ? `?${orgScopeQs}` : ""} />

          <div className="repo-toolbar">
            <button className="btn-approve" disabled={syncingAll} onClick={() => void syncAll()} type="button">
              {syncingAll ? <><Spinner /> Sincronizando…</> : "Sincronizar todos"}
            </button>
            <button className="btn-reject" disabled={docState.status === "running"} onClick={() => regen("")} type="button">
              {docState.status === "running" ? <><Spinner /> Gerando docs…</> : "Regerar documentação"}
            </button>
            <button className="btn-reject" onClick={() => setViewDoc({ relPath: WORKSPACE_DOC_REL, title: `Visão geral — ${ws.name}` })} type="button">Ver visão geral</button>
            <button className="btn-reject" onClick={() => void downloadAllDocs()} type="button">Baixar docs (.md)</button>
            <button className="btn-reject" disabled={busy} onClick={() => setEditing(true)} type="button">Editar</button>
            <button className="btn-reject" disabled={busy} onClick={() => setConfirmState({ title: "Desconectar todos", message: `Desconectar TODOS os repositórios de "${ws.name}"? Os clones serão apagados.`, confirmLabel: "Desconectar", onConfirm: () => void mutate(`/api/admin/workspaces/${ws.id}/repo`, "DELETE") })} type="button">Desconectar todos</button>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 680 }}>
          <label className="field">
            <span>Conexão (para listar/credenciar)</span>
            <select value={connectionId} onChange={(e) => setConnectionId(e.target.value)}>
              <option value="">(sem conexão — token inline / público)</option>
              {connections.map((c) => (
                <option key={c.id} value={c.id}>{c.label} ({c.provider}/{c.host})</option>
              ))}
            </select>
          </label>
          {connectionId && (
            <RepoPicker
              options={repoOptions}
              loading={loadingRepos}
              warnings={repoWarnings}
              selectedUrls={selectedUrls}
              onToggle={toggleRepo}
              onToggleGroup={toggleGroup}
            />
          )}

          {drafts.length > 0 && (
            <div className="field">
              <span>Selecionados ({drafts.length}) — defina o branch (opcional) ou remova</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {drafts.map((d) => (
                  <div key={d.url} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontFamily: "monospace", fontSize: 12, flex: "1 1 220px", overflow: "hidden", textOverflow: "ellipsis" }} title={d.url}>
                      {repoPathName(d.url)}
                    </span>
                    <input
                      value={d.branch}
                      onChange={(e) => patchDraft(d.url, { branch: e.target.value })}
                      placeholder="branch (default)"
                      spellCheck={false}
                      style={{ flex: "0 0 150px" }}
                    />
                    <button className="btn-reject" type="button" onClick={() => removeDraft(d.url)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label className="field">
            <span>Adicionar por URL (https){connectionId ? " — ou use a lista acima" : ""}</span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
                placeholder="https://github.com/org/repo.git"
                spellCheck={false}
                style={{ flex: "1 1 240px" }}
              />
              <button className="btn-reject" type="button" onClick={addManual} disabled={!manualUrl.trim()}>Adicionar</button>
            </div>
          </label>
          {!connectionId && (
            <label className="field">
              <span>Token inline (opcional, aplicado ao próximo repo adicionado por URL)</span>
              <input type="password" value={manualToken} onChange={(e) => setManualToken(e.target.value)} placeholder="PAT para repo privado (ou vazio p/ público)" spellCheck={false} />
            </label>
          )}

          <div className="admin-user-actions">
            <button className="btn-approve" disabled={busy} onClick={save} type="button">Salvar e sincronizar</button>
            {ws.repos.length > 0 && <button className="btn-reject" disabled={busy} onClick={() => { setDrafts(ws.repos.map((r) => ({ url: r.url, branch: r.branch ?? "", connectionId: r.connectionId ?? "" }))); setEditing(false); }} type="button">Cancelar</button>}
          </div>
        </div>
      )}

      {renaming !== null && (
        <PromptModal
          title="Renomear grupo"
          label={`Nome de exibição para o grupo "${renaming}" (vazio = nome original):`}
          initial={ws.groupLabels[renaming] ?? ""}
          placeholder="Ex.: Backend, Cliente A…"
          onSubmit={submitRename}
          onClose={() => setRenaming(null)}
        />
      )}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onClose={() => setConfirmState(null)}
        />
      )}
      {viewDoc && (
        <DocViewerModal wsId={ws.id} relPath={viewDoc.relPath} title={viewDoc.title} onClose={() => setViewDoc(null)} />
      )}
    </div>
  );
}

/**
 * Visor de um documento gerado (modal). Carrega o markdown sob demanda e permite
 * baixar. Não fica em polling - abre, lê, fecha.
 */
function DocViewerModal({
  wsId,
  relPath,
  title,
  onClose,
}: {
  wsId: string;
  relPath: string;
  title: string;
  onClose: () => void;
}) {
  const [content, setContent] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    // 404 = doc ainda não gerado -> estado vazio (sem erro)
    fetchOrg(`/api/admin/workspaces/${wsId}/docs/content?path=${encodeURIComponent(relPath)}`)
      .then((r) => (r.ok ? r.json() : { content: "" }))
      .then((j) => { if (alive) setContent(typeof j.content === "string" ? j.content : ""); })
      .catch(() => alive && setContent(""));
    return () => { alive = false; };
  }, [wsId, relPath]);

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div className="modal doc-viewer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <header className="modal-header">
          <h2 style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="btn-reject"
              type="button"
              disabled={!content}
              onClick={() => content != null && downloadText(relPath.split("/").pop() || "doc.md", content)}
            >
              Baixar
            </button>
            <button className="modal-close" onClick={onClose} aria-label="Fechar" type="button">×</button>
          </div>
        </header>
        <div className="doc-viewer-body">
          {content === null || content.trim() === "" ? (
            <div className="doc-viewer-empty"><span className="repo-spin" aria-label="gerando" /></div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripMarkdownFence(content)}</ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
}

interface TrashItem {
  id: string;
  orgName: string | null;
  workspaceId: string;
  workspaceName: string;
  url: string;
  provider: string;
  branch: string | null;
  lastCommit: string | null;
  workspaceExists: boolean;
  trashedAt: string;
  trashedByEmail: string | null;
}

/**
 * Lixeira de repositórios (só sysadmin): o que os usuários "removeram" fica aqui
 * fisicamente preservado. Dá pra restaurar pro workspace ou apagar de vez.
 */
function TrashPanel({ setErr }: { setErr: (e: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmState, setConfirmState] = useState<{ title: string; message: string; confirmLabel?: string; onConfirm: () => void } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchOrg("/api/admin/trash/repos");
      const j = await r.json().catch(() => ({ items: [] }));
      setItems(j.items ?? []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (open) void load(); }, [open, load]);

  const act = async (path: string, method: string) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetchOrg(path, { method });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setErr(j.error || "falha"); }
    } finally {
      setBusy(false);
      await load();
    }
  };

  return (
    <div className="admin-block" style={{ marginTop: 24 }}>
      <h2 style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }} onClick={() => setOpen((o) => !o)}>
        <span>{open ? "▾" : "▸"} 🗑 Lixeira de repositórios</span>
        <span className="admin-user-meta">operador da plataforma · remoções recuperáveis</span>
        {open && items.length > 0 && <span className="repo-pill">{items.length}</span>}
      </h2>
      {open && (
        loading ? (
          <div className="admin-user-meta">carregando…</div>
        ) : items.length === 0 ? (
          <div className="admin-user-meta">lixeira vazia.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((t) => (
              <div key={t.id} className="repo-item">
                <div className="repo-item-main">
                  <span className="badge">{t.provider}</span>
                  <span className="repo-item-name" title={t.url}>{repoPathName(t.url)}</span>
                  <span className="repo-item-meta">
                    {t.workspaceName}{t.orgName ? ` · ${t.orgName}` : ""} · removido {relTime(t.trashedAt)}
                    {t.trashedByEmail ? ` por ${t.trashedByEmail}` : ""}
                    {!t.workspaceExists ? " · workspace não existe mais" : ""}
                  </span>
                  <div className="repo-item-actions">
                    <button
                      className="icon-btn"
                      disabled={busy || !t.workspaceExists}
                      title={t.workspaceExists ? "Restaurar pro workspace de origem" : "O workspace de origem não existe mais"}
                      onClick={() => void act(`/api/admin/trash/repos/${t.id}/restore`, "POST")}
                      type="button"
                    >
                      ↩ restaurar
                    </button>
                    <button
                      className="icon-btn danger"
                      disabled={busy}
                      onClick={() => setConfirmState({
                        title: "Apagar definitivamente",
                        message: `Apagar PERMANENTEMENTE "${shortRepoName(t.url)}" da lixeira? Esta ação não tem volta.`,
                        confirmLabel: "Apagar de vez",
                        onConfirm: () => void act(`/api/admin/trash/repos/${t.id}`, "DELETE"),
                      })}
                      type="button"
                    >
                      🗑 apagar de vez
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          onConfirm={confirmState.onConfirm}
          onClose={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}
